import type { ModuleKey } from "@/lib/modules/types"
import type { AreaDeModulo } from "@/lib/modules/areas"

/**
 * Cuanto cuesta cada modulo (fase 5, spec 2026-09-15).
 *
 * Los importes viven AQUI y no en la base de datos, a proposito: cambiar un
 * precio pasa por revision de codigo y por el script que sincroniza Stripe, que
 * es el control que hoy no existe --hoy los precios estan en lib/billing/plans.ts
 * y el webhook identifica el plan comparando el importe recibido--.
 *
 * El TIPO de precio tambien se declara aqui y no se deriva del area en ejecucion:
 * `ModuleDefinition` (lib/modules/types.ts) no lleva `area`, y `getModuleCatalog`
 * devuelve `null` cuando no puede leer la tabla, asi que derivarlo dejaria al
 * cobro sin poder calcular nada durante un fallo de lectura. El area sigue siendo
 * la regla con la que se eligieron los tipos, y precios.test.ts vigila que no se
 * separen.
 */

/**
 * El area sale de `lib/modules/areas.ts` y NO se vuelve a declarar aqui.
 *
 * Estaba escrita palabra por palabra en los dos sitios, y como los dos tipos
 * eran estructuralmente identicos TypeScript no los enfrentaba nunca: el dia
 * que entrara un area nueva la habria recibido el de `areas.ts` --que es el que
 * pinta el selector de modulos-- y no el de precios, y un area que faltara en
 * el orden de `factura-de-modulos.ts` se habria quedado sin pack sin dar error.
 *
 * Es un import de TIPO: no lee el catalogo en ejecucion, asi que no toca la
 * decision de no derivar el area. Se reexporta para no romper a quien ya lo
 * importa desde aqui.
 */
export type { AreaDeModulo }

/** Por empleado activo, o plano por empresa. */
export type TipoDePrecio = "por_empleado" | "plano"

export type Intervalo = "mensual" | "anual"

export interface PrecioDeModulo {
  area: AreaDeModulo
  tipo: TipoDePrecio
  /** En centimos. Para `por_empleado`, por empleado y mes. */
  centimosMensuales: number
}

/** Los modulos que se venden. Los cuatro del nucleo no estan, ni los de SIN_PRECIO_TODAVIA. */
export const MODULOS_VENDIBLES = [
  "fichaje", "workforce", "teletrabajo", "turnos", "tareas", "ausencias", "nominas", "documentos", "sugerencias",
  "inventario", "compras", "producciones", "gastos", "banca",
  "facturacion", "tickets", "cartas_pedidos", "reservas",
  "informes", "chat", "proyectos",
] as const satisfies readonly ModuleKey[]

export type ModuloVendible = (typeof MODULOS_VENDIBLES)[number]

/**
 * Modulos que existen pero TODAVIA no se cobran: se encienden a mano.
 *
 * No es un olvido, es una decision de precio que no se puede tomar de pasada. El
 * descuento por pack de area se calcula por PROPORCION --con todos los modulos de un
 * area, un 30 %-- y `CUANTOS_POR_AREA` sale de este catalogo. Meter un modulo nuevo en
 * Operaciones sube ese total de cinco a seis, y quien hoy tiene los cinco deja de
 * tenerlos "todos": pierde el 30 %, se queda en el 20 % y **le sube la factura sin
 * haber contratado nada**. Con los numeros del spec, esa cadena pasaria de 50,90 € a
 * 52,40 € al mes.
 *
 * Asi que Tostador nace fuera del cobro. El resto del sistema ya sabe convivir con
 * claves que no se venden (`personal` se retiro, `cierre_caja` se fusiono en
 * `tickets`): se ignoran al armar la suscripcion en vez de reventar.
 */
export const SIN_PRECIO_TODAVIA: readonly ModuleKey[] = ["tostador"]

/**
 * LAS TARIFAS. Cada empresa tiene la suya (`companies.tarifa`), y su cobro se
 * calcula siempre con ella.
 *
 * - `legacy`: la de septiembre de 2026 (5 € de nucleo, 0,50 € por persona en
 *   Equipo, 3 € el resto). La conservan las empresas que ya estaban, por
 *   decision del dueño del producto (6 oct 2026).
 * - `2026-10`: la vigente para las altas nuevas. Mas cara: el producto lleva IA
 *   en los modulos (lectura de facturas, nominas, albaranes, recetas) incluida
 *   en el precio, y Chat y Proyectos pasan a cobrarse por persona, como
 *   cualquier herramienta de equipo. Los agentes se cobran aparte, por uso
 *   (lib/ia/coste.ts).
 *
 * La tarifa va escrita en la clave de busqueda del Precio de Stripe (ver
 * `claveDeBusqueda`), asi que una linea de una suscripcion DICE con que tarifa
 * se cobra y quien la lee no necesita preguntar a la base.
 *
 * El area y el pack no cambian entre tarifas: solo importes y tipo.
 */
export type Tarifa = "legacy" | "2026-10"

/** La de las empresas nuevas y la de la web. */
export const TARIFA_VIGENTE: Tarifa = "2026-10"

export const TARIFAS = ["legacy", "2026-10"] as const satisfies readonly Tarifa[]

/** De la columna de la base a una tarifa. Lo desconocido cae a legacy: nunca se cobra de mas por un valor raro. */
export function tarifaDeLaBase(valor: string | null | undefined): Tarifa {
  return (TARIFAS as readonly string[]).includes(valor ?? "") ? (valor as Tarifa) : "legacy"
}

export interface TablaDePrecios {
  /** La cuota fija del nucleo, por empresa y mes, en centimos. */
  nucleo: number
  modulos: Record<ModuloVendible, PrecioDeModulo>
}

const EQUIPO = { area: "equipo", tipo: "por_empleado", centimosMensuales: 50 } as const
const PLANO = (area: AreaDeModulo) => ({ area, tipo: "plano", centimosMensuales: 300 }) as const

const LEGACY: TablaDePrecios = {
  nucleo: 500,
  modulos: {
    fichaje: EQUIPO,
    workforce: EQUIPO,
    // Se cobra por empleado, como el resto del area de equipo: lo que consume es
    // una persona con un equipo, no la empresa.
    teletrabajo: EQUIPO,
    turnos: EQUIPO,
    tareas: EQUIPO,
    ausencias: EQUIPO,
    nominas: EQUIPO,
    documentos: EQUIPO,
    sugerencias: EQUIPO,
    inventario: PLANO("operaciones"),
    compras: PLANO("operaciones"),
    producciones: PLANO("operaciones"),
    gastos: PLANO("operaciones"),
    banca: PLANO("operaciones"),
    facturacion: PLANO("ventas"),
    tickets: PLANO("ventas"),
    cartas_pedidos: PLANO("ventas"),
    reservas: PLANO("ventas"),
    informes: PLANO("direccion"),
    chat: PLANO("direccion"),
    proyectos: PLANO("direccion"),
  },
}

const EQUIPO_2610 = { area: "equipo", tipo: "por_empleado", centimosMensuales: 150 } as const
const PLANO_2610 = (area: AreaDeModulo) => ({ area, tipo: "plano", centimosMensuales: 1500 }) as const
/** Chat y Proyectos: herramientas de equipo, por persona. */
const POR_PERSONA_2610 = { area: "direccion", tipo: "por_empleado", centimosMensuales: 200 } as const

const T2610: TablaDePrecios = {
  nucleo: 1900,
  modulos: {
    fichaje: EQUIPO_2610,
    workforce: EQUIPO_2610,
    teletrabajo: EQUIPO_2610,
    turnos: EQUIPO_2610,
    tareas: EQUIPO_2610,
    ausencias: EQUIPO_2610,
    nominas: EQUIPO_2610,
    documentos: EQUIPO_2610,
    sugerencias: EQUIPO_2610,
    inventario: PLANO_2610("operaciones"),
    compras: PLANO_2610("operaciones"),
    producciones: PLANO_2610("operaciones"),
    gastos: PLANO_2610("operaciones"),
    banca: PLANO_2610("operaciones"),
    facturacion: PLANO_2610("ventas"),
    tickets: PLANO_2610("ventas"),
    cartas_pedidos: PLANO_2610("ventas"),
    reservas: PLANO_2610("ventas"),
    informes: PLANO_2610("direccion"),
    chat: POR_PERSONA_2610,
    proyectos: POR_PERSONA_2610,
  },
}

export const TABLAS_DE_PRECIOS: Record<Tarifa, TablaDePrecios> = { legacy: LEGACY, "2026-10": T2610 }

/** El precio de un modulo en una tarifa. Sin tarifa, legacy: el lado que nunca cobra de mas. */
export function precioDe(modulo: ModuloVendible, tarifa: Tarifa = "legacy"): PrecioDeModulo {
  return TABLAS_DE_PRECIOS[tarifa].modulos[modulo]
}

export function nucleoDe(tarifa: Tarifa = "legacy"): number {
  return TABLAS_DE_PRECIOS[tarifa].nucleo
}

/**
 * Los de la tarifa legacy, con su nombre de siempre. El AREA es la misma en
 * todas las tarifas, asi que para el area vale cualquiera; para importe y tipo,
 * `precioDe(modulo, tarifa)`.
 */
export const NUCLEO_CENTIMOS_MENSUALES = LEGACY.nucleo
export const PRECIOS_POR_MODULO: Record<ModuloVendible, PrecioDeModulo> = LEGACY.modulos

/**
 * El anual es doce veces el mensual, SIN descuento. No es un olvido: es lo que
 * hay hoy, y el comentario de lib/billing/plans.ts:300-302 dice que fue una
 * peticion expresa del dueño del producto.
 */
export function centimosDelIntervalo(centimosMensuales: number, intervalo: Intervalo): number {
  return intervalo === "anual" ? centimosMensuales * 12 : centimosMensuales
}

const SUFIJO: Record<Intervalo, string> = { mensual: "mes", anual: "ano" }

/**
 * La clave con la que Stripe encuentra el Precio, y con la que el webhook sabe
 * que linea es cual. Se deriva y no se guarda: una clave guardada a mano se
 * separa del modulo el dia que alguien se equivoca de letra.
 */
/**
 * Legacy conserva las claves de siempre (`zindra_modulo_fichaje_mes`); cada
 * tarifa nueva lleva su marca (`zindra_t2610_modulo_fichaje_mes`). Asi las
 * suscripciones que ya existan no se tocan y cada linea dice su tarifa.
 */
const MARCA_DE_TARIFA: Record<Tarifa, string> = { legacy: "", "2026-10": "t2610_" }

export function claveDeBusqueda(modulo: ModuloVendible, intervalo: Intervalo, tarifa: Tarifa = "legacy"): string {
  return `zindra_${MARCA_DE_TARIFA[tarifa]}modulo_${modulo}_${SUFIJO[intervalo]}`
}

export function claveDeBusquedaDelNucleo(intervalo: Intervalo, tarifa: Tarifa = "legacy"): string {
  return `zindra_${MARCA_DE_TARIFA[tarifa]}nucleo_${SUFIJO[intervalo]}`
}

/**
 * El intervalo tiene CUATRO grafias, y las conversiones viven aqui.
 *
 * - `"mensual"`/`"anual"` es la nuestra (`Intervalo`).
 * - `"monthly"`/`"annual"` es la de la base y la de las acciones
 *   (`companies.billing_interval`, lib/actions/billing.ts).
 * - `"month"`/`"year"` es la de Stripe (`price.recurring.interval`).
 *
 * Estaban escritas a mano en dos sitios de esta rama —el informe de la
 * migracion y el script de Precios— y la parte B iba a escribir la tercera. Una
 * conversion repetida es una conversion que algun dia dice cosas distintas en
 * cada copia, y esta decide si a una empresa se le cobra un mes o un año.
 */
export type IntervaloDeLaBase = "monthly" | "annual"
export type IntervaloDeStripe = "month" | "year"

const DE_LA_BASE: Record<IntervaloDeLaBase, Intervalo> = { monthly: "mensual", annual: "anual" }
const PARA_LA_BASE: Record<Intervalo, IntervaloDeLaBase> = { mensual: "monthly", anual: "annual" }
const DE_STRIPE: Record<IntervaloDeStripe, Intervalo> = { month: "mensual", year: "anual" }
const PARA_STRIPE: Record<Intervalo, IntervaloDeStripe> = { mensual: "month", anual: "year" }

/**
 * De la grafia de la base a la nuestra. Lo que no reconoce cae a mensual, que es
 * lo que hace el codigo vivo: `companies.billing_interval` tiene DEFAULT
 * 'monthly' (scripts/027-billing-schema.sql:30) y hay filas vacias.
 */
export function intervaloDeLaBase(valor: string | null | undefined): Intervalo {
  return DE_LA_BASE[valor as IntervaloDeLaBase] ?? "mensual"
}

export function intervaloParaLaBase(intervalo: Intervalo): IntervaloDeLaBase {
  return PARA_LA_BASE[intervalo]
}

/**
 * De la grafia de Stripe a la nuestra. Devuelve null para lo que no es uno de
 * nuestros dos intervalos —Stripe tambien tiene `day` y `week`—: igual que
 * `deLaClaveDeBusqueda`, un webhook no adivina.
 */
export function intervaloDeStripe(valor: string | null | undefined): Intervalo | null {
  return DE_STRIPE[valor as IntervaloDeStripe] ?? null
}

export function intervaloParaStripe(intervalo: Intervalo): IntervaloDeStripe {
  return PARA_STRIPE[intervalo]
}

/** Los dos intervalos, para recorrerlos sin escribirlos a mano en cada sitio. */
export const INTERVALOS = ["mensual", "anual"] as const satisfies readonly Intervalo[]

/**
 * La vuelta: de la clave de una linea de Stripe al modulo y al intervalo.
 *
 * Es lo que usara el webhook para saber que linea es cual, en lugar de comparar
 * el importe recibido contra unas constantes, que es lo que hace hoy y lo que
 * impide tocar un precio sin romper suscripciones vivas.
 *
 * La comparacion viva esta en `app/api/webhooks/stripe/route.ts`: en el caso
 * `checkout.session.completed` (lineas 95-103) y en `customer.subscription.updated`
 * (lineas 256-298), las dos contra `PLAN_BASE_PRICES_*` y `PLAN_EMPLOYEE_PRICES_*`.
 * `lib/actions/billing.ts` repite la misma comparacion en la sincronizacion
 * manual. En `lib/billing/plans.ts` solo estan las CONSTANTES (desde la linea
 * 287): sus lineas 155-165 son PLAN_FEATURES y no comparan nada.
 *
 * Devuelve null para cualquier clave que no sea nuestra: un webhook no adivina.
 */
export function deLaClaveDeBusqueda(
  clave: string,
): { concepto: "nucleo" | ModuloVendible; intervalo: Intervalo; tarifa: Tarifa } | null {
  for (const tarifa of TARIFAS) {
    for (const intervalo of ["mensual", "anual"] as const) {
      if (clave === claveDeBusquedaDelNucleo(intervalo, tarifa)) return { concepto: "nucleo", intervalo, tarifa }
      for (const modulo of MODULOS_VENDIBLES) {
        if (clave === claveDeBusqueda(modulo, intervalo, tarifa)) return { concepto: modulo, intervalo, tarifa }
      }
    }
  }
  return null
}
